import { defineStore } from 'pinia';
import axios from 'axios';
import authApi from '../api/auth';

function isAuthError(error: unknown): boolean {
	// The client interceptor tags session/token errors (services-sam returns
	// these as HTTP 200 with ok === false, so they reject as plain Errors).
	if (
		error instanceof Error &&
		(error as Error & { isAuthError?: boolean }).isAuthError
	) {
		return true;
	}
	// Fallback for genuine HTTP-level auth failures.
	if (axios.isAxiosError(error) && error.response?.status === 401) {
		return true;
	}
	return false;
}

interface AuthUser {
	id: number;
	email: string;
	name: string;
	picture_url?: string | null;
}

interface AuthState {
	token: string | null;
	user: AuthUser | null;
	loading: boolean;
	error: string | null;
}

const TOKEN_KEY = 'libro_admin_token';

export const useAuthStore = defineStore('auth', {
	state: (): AuthState => ({
		token: localStorage.getItem(TOKEN_KEY) || null,
		user: null,
		loading: false,
		error: null,
	}),

	getters: {
		isAuthenticated: (state) => !!state.token,
		currentUser: (state) => state.user,
		hasError: (state) => !!state.error,
	},

	actions: {
		async googleLogin(credential: string) {
			this.loading = true;
			this.error = null;
			try {
				const response =
					await authApi.googleLogin(credential);
				this.token = response.data.token;
				this.user = {
					id: response.data.user.id,
					email: response.data.user.email,
					name: response.data.user.name,
					picture_url:
						response.data.user.picture_url,
				};
				localStorage.setItem(
					TOKEN_KEY,
					response.data.token,
				);
			} catch (error) {
				this.error =
					error instanceof Error
						? error.message
						: 'Unknown error';
				console.error(
					'Error with Google login:',
					error,
				);
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async checkAuth() {
			if (!this.token) {
				return false;
			}

			this.loading = true;
			this.error = null;
			try {
				const response = await authApi.getMe();
				this.user = response.data;
				return true;
			} catch (error) {
				if (isAuthError(error)) {
					this.logout();
					return false;
				}
				throw error;
			} finally {
				this.loading = false;
			}
		},

		logout() {
			this.token = null;
			this.user = null;
			localStorage.removeItem(TOKEN_KEY);
		},

		clearError() {
			this.error = null;
		},
	},
});
